/**
 * 운동 기록 서버 연동 (TC_EXRC_MNG / TO_USR_EXRC_HIST).
 * 운동 기록 화면 3개(기록 메인 / 운동 추가 / 기록 완료)가 이 파일만 쓴다.
 */
import axiosApi from './daonAxios';

const authOption = () => ({
    headers: {
        Authorization: `Bearer ${localStorage.getItem('jToken')}`,
    },
});

/**
 * 운동 강도 공통코드 (tc_cd_dtl.cd_id, 그룹 EXRC_IN_CD).
 * 화면은 light/normal/hard 로 쓰고 서버로는 이 코드로 바꿔 보낸다.
 */
export const EXRC_IN_CD = {
    light: 'L',
    normal: 'N',
    hard: 'H',
};

/** 서버 코드 -> 화면 키 (기록을 다시 불러와 수정할 때 쓴다) */
export const toIntensityKey = (cd) =>
    Object.keys(EXRC_IN_CD).find((k) => EXRC_IN_CD[k] === cd) || '';

/** "2026-08-06" -> "20260806" (DB REC_DE 는 varchar(8)) */
export const toRecDe = (dateStr) => String(dateStr).replace(/-/g, '');

/** "20260806" -> "2026-08-06" */
export const fromRecDe = (recDe) =>
    `${recDe.slice(0, 4)}-${recDe.slice(4, 6)}-${recDe.slice(6, 8)}`;

/** 현재 시각을 "HHmmss" 로 (DB REC_TM 은 varchar(6)) */
export const nowRecTm = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
};

/** "072000" -> "오전 07:20" */
export const formatRecTm = (recTm) => {
    if (!recTm || recTm.length < 4) return '';
    const hour = Number(recTm.slice(0, 2));
    const min = recTm.slice(2, 4);
    const isAm = hour < 12;
    const h12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${isAm ? '오전' : '오후'} ${String(h12).padStart(2, '0')}:${min}`;
};

/** 종목 검색. 운동 추가 화면 자동완성용. */
export const getExerciseTypes = async (keyword) => {
    const res = await axiosApi.get('/exercise/types', {
        ...authOption(),
        params: { keyword: keyword || '' },
        noLoading: true, // 타이핑할 때마다 부르므로 전역 로딩을 띄우지 않는다
    });
    return res.data?.result ?? [];
};

/** 해당 일자의 기록 목록 + 목표 달성률. 운동 기록 화면 전체가 이걸로 그려진다. */
export const getExerciseList = async (recDe) => {
    const res = await axiosApi.get('/exercise/list', {
        ...authOption(),
        params: { recDe },
    });
    return res.data?.result ?? null;
};

/** 기간 내 일자별 합계. 날짜 선택 바의 점 표시용. */
export const getExerciseSummary = async (startDe, endDe) => {
    const res = await axiosApi.get('/exercise/summary', {
        ...authOption(),
        params: { startDe, endDe },
        noLoading: true, // 날짜 바는 화면 뒤에서 조용히 갱신한다
    });
    return res.data?.result ?? [];
};

/** 운동 기록 등록. */
export const saveExercise = async (record) => {
    const res = await axiosApi.post('/exercise', record, authOption());
    return res.data?.result === true;
};

/** 운동 기록 수정. 연계(위치) 기록이면 서버가 false 를 준다. */
export const updateExercise = async (usrExrcHistId, record) => {
    const res = await axiosApi.put(`/exercise/${usrExrcHistId}`, record, authOption());
    return res.data?.result === true;
};

/** 운동 기록 삭제. 연계(워치) 기록이면 서버가 false 를 준다. */
export const deleteExercise = async (usrExrcHistId) => {
    const res = await axiosApi.delete(`/exercise/${usrExrcHistId}`, authOption());
    return res.data?.result === true;
};

/** 운영 WAF 가 8KB 넘는 요청 바디를 403 으로 막는다. 여유를 두고 이 크기 아래로 나눠 보낸다. */
const SYNC_MAX_BODY_BYTES = 6 * 1024;

const utf8Bytes = (s) => new TextEncoder().encode(s).length;

/**
 * 직렬화 크기가 maxBytes 를 넘지 않게 묶는다.
 * 한 건이 혼자 넘으면 버린다 — 그 건 때문에 매번 403 이 나면 뒤 묶음까지 영영 못 올라간다.
 */
const chunkByBytes = (list, maxBytes) => {
    const chunks = [];
    let cur = [];
    let curBytes = 2; // '[' ']'

    list.forEach((item) => {
        const bytes = utf8Bytes(JSON.stringify(item)) + 1; // ','
        if (bytes + 2 > maxBytes) {
            console.warn(`[exercise] 기록 1건이 ${bytes}B 라 전송 제외 — id=${item.exrcRecId}`);
            return;
        }
        if (cur.length > 0 && curBytes + bytes > maxBytes) {
            chunks.push(cur);
            cur = [];
            curBytes = 2;
        }
        cur.push(item);
        curBytes += bytes;
    });
    if (cur.length > 0) chunks.push(cur);

    return chunks;
};

/**
 * 워치 운동 기록 동기화. 삼성 헬스에서 읽은 걸 그대로 올린다.
 * 중간에 실패하면 거기서 멈추고 던진다. 서버가 기록 고유값으로 중복을 거르니 다음에 다시 보내도 된다.
 */
export const syncWatchExercise = async (list) => {
    if (!list || list.length === 0) return 0;

    let saved = 0;
    for (const chunk of chunkByBytes(list, SYNC_MAX_BODY_BYTES)) {
        const res = await axiosApi.post('/exercise/sync', chunk, {
            ...authOption(),
            noLoading: true, // 화면 뒤에서 도는 작업이라 로딩을 띄우지 않는다
        });
        saved += res.data?.result ?? 0;
    }
    return saved;
};


/** 환자의 최신 체중 조회 */
export const getLatestWeight = async () => {
    const res = await axiosApi.get('/exercise/latestWeight', {
        ...authOption(),
    });

    return res.data?.result ?? null;
};